/**
 * Chart Scheduler (FS-04, slice). Emits hazard spawns quantised to the beat grid, each with a lead
 * time so it reaches the ship exactly on its target beat: leadTime = |SPAWN_Z| / scrollSpeed. Every
 * random draw goes through the run Rng (Design Bible §4) so the whole run is reproducible.
 *
 * Node width is derived so it is never below MIN_NODE_WIDTH at the passable radius — the guarantee is
 * enforced here at "bake" time and by the shared node mask at runtime.
 */
import {
  AXIS_PRESSURE_MIN_INTENSITY,
  AXIS_PRESSURE_PERIOD,
  MIN_NODE_WIDTH,
  PLAY_RADIUS,
  POLARITY_FLIP_MIN_BEATS,
  SHIP_HITBOX_RADIUS,
  SPAWN_Z,
  TUNNEL_RADIUS,
} from '../core/constants';
import { clamp, lerp, wrap2pi } from '../core/math-det';
import type { Rng } from '../core/rng';
import type { HazardKind, PowerUpName } from '../core/event-map';
import type { AudioClock } from '../audio/clock';
import type { Director } from './director';
import { HAZARD_KINDS } from './hazards/kinds';
import {
  NODE_BAND_WIDTH,
  NODE_RADIAL_MARGIN,
  NODE_RMIN_FLOOR,
  REACH_AXIS_MAX,
  REACH_AXIS_MIN,
  REACH_RIM_MAX,
  REACH_RIM_MIN,
  type HazardKindDef,
  type HazardParams,
  type HazardPolarity,
} from './hazards/types';

export interface SpawnRequest {
  def: HazardKindDef;
  params: HazardParams;
  z: number;
  targetBeat: number;
  intensity: number;
  /** `false` spawns a Phantom (FS-01 R08). Only onboarding sets it; the live chart is always lethal. */
  lethal?: boolean;
  /** A pickup riding this hazard's node — onboarding's scripted first shield (FS-01 §5.2). */
  pickup?: PowerUpName;
}

// Which kinds may appear, weighted. Slab/ring provide the "must-move" pressure; ribbons the graze layer.
interface KindWeight {
  kind: HazardKind;
  minIntensity: number;
  maxIntensity?: number;
  weight: number;
}
const KIND_TABLE: KindWeight[] = [
  { kind: 'SineRibbon', minIntensity: 0.0, weight: 3 },
  { kind: 'TriangleFence', minIntensity: 0.0, weight: 2 },
  { kind: 'StandingWave', minIntensity: 0.0, weight: 2 },
  { kind: 'SawtoothComb', minIntensity: 0.15, weight: 2 },
  { kind: 'PulseRing', minIntensity: 0.2, weight: 2 },
  { kind: 'SquareGate', minIntensity: 0.3, weight: 2 },
  { kind: 'HelixBraid', minIntensity: 0.3, weight: 2 },
  { kind: 'BeatGate', minIntensity: 0.4, weight: 2 },
  { kind: 'SubBassSlab', minIntensity: 0.35, weight: 2 },
  // ChirpSweep needs the most telegraph lead — kept off the top speed tier (FS-06 §5.3).
  { kind: 'ChirpSweep', minIntensity: 0.35, maxIntensity: 0.88, weight: 2 },
  { kind: 'NoiseCloud', minIntensity: 0.55, weight: 1 },
  { kind: 'FeedbackLoop', minIntensity: 0.6, weight: 1 },
];

const _params: HazardParams = {
  amp: 0,
  freq: 0,
  phase: 0,
  nodeTheta: 0,
  nodeHalfTheta: 0,
  nodeRMin: 0,
  nodeRMax: PLAY_RADIUS,
  polarity: 'rim',
  reach: 1,
  count: 0,
  rate: 0,
  seed: 0,
};

/**
 * Build a hazard's immutable params for a given kind, intensity and node position. The node
 * half-width is derived so the node is never below MIN_NODE_WIDTH at the passable radius (FS-06 R10).
 * Shared by the scheduler and the boss so the guarantee has one home.
 */
export function configureParams(
  def: HazardKindDef,
  intensity: number,
  nodeTheta: number,
  rng: Rng,
  clock: AudioClock,
  out: HazardParams,
  polarity: HazardPolarity = 'rim',
): void {
  out.amp = 0;
  out.freq = 2;
  out.phase = rng.range(0, Math.PI * 2);
  out.count = 4;
  out.rate = 0;
  out.seed = rng.nextUint32();
  // Polarity is resolved before scaling so `scaleByIntensity` can pick the right reach range.
  out.polarity = def.polarities.includes(polarity) ? polarity : 'rim';
  out.reach =
    out.polarity === 'axis'
      ? lerp(REACH_AXIS_MIN, REACH_AXIS_MAX, intensity)
      : lerp(REACH_RIM_MIN, REACH_RIM_MAX, intensity);
  def.scaleByIntensity(intensity, out);

  if (def.kind === 'BeatGate') out.rate = clock.bps;

  out.nodeTheta = nodeTheta;

  let passableR: number;
  if (def.kind === 'SubBassSlab') passableR = 10.5;
  else if (def.kind === 'PulseRing') passableR = TUNNEL_RADIUS;
  else passableR = Math.max(TUNNEL_RADIUS - out.amp, 2.5);
  let halfTheta = MIN_NODE_WIDTH / (2 * passableR) + 0.06;
  if (def.kind === 'SubBassSlab') halfTheta *= 1.5;
  out.nodeHalfTheta = clamp(halfTheta, 0.14, 0.7);

  // The node's radial band (FS-06 R38). `nodeRMin` is floored so the arc-wise relief at the band's
  // inner edge still clears the hitbox: an angular gap has no width near the axis, so a node that
  // did not declare a radius would be unreachable in axis polarity.
  const requiredRMin = (SHIP_HITBOX_RADIUS + NODE_RADIAL_MARGIN) / out.nodeHalfTheta;
  const floorR = Math.max(NODE_RMIN_FLOOR, requiredRMin);
  // The band's *position* is the difficulty channel (R40, and Design Bible §2.1 C1): a node that
  // spans every radius on its bearing is a wedge, and a wedge leaves a permanently safe line.
  // Drawing rMin per hazard is what forces radial movement rather than only lateral movement.
  const span = Math.max(PLAY_RADIUS - NODE_BAND_WIDTH - floorR, 0);
  out.nodeRMin = floorR + rng.next() * span;
  out.nodeRMax = out.nodeRMin + NODE_BAND_WIDTH;

  if (def.kind === 'PulseRing') {
    out.nodeTheta = 0;
    out.nodeHalfTheta = 0;
    out.nodeRMin = 0;
    out.nodeRMax = PLAY_RADIUS;
  }
}

/**
 * What `Game` needs from a scheduler. Two implement it: `ChartScheduler` (the live, stochastic one)
 * and `OnboardingScheduler` (FS-01 §5.2's authored script). `loadSegment` swaps the instance and
 * `Game.step` is untouched — a mode, or a segment, contributes data rather than a code path
 * (FS-09 R02, FS-08 R07).
 */
export interface Scheduler {
  setAllowList(kinds: readonly HazardKind[]): void;
  setPolarityMix(mix: number): void;
  reset(): void;
  update(director: Director, emit: (req: SpawnRequest) => void): void;
  /** The last node placed — pickups site themselves relative to it (FS-07 R29). */
  readonly lastNodeTheta: number;
  readonly lastNodeHalfTheta: number;
  /** True once the segment's content has finished. Only the scripted scheduler ever ends. */
  readonly done: boolean;
}

export class ChartScheduler implements Scheduler {
  private nextBeatIndex = 0;
  private prevNodeTheta = 0;
  /** The last node placed — pickups site themselves relative to it (FS-07 R29). */
  lastNodeTheta = 0;
  lastNodeHalfTheta = 0.2;
  /** Polarity cadence state (FS-08 §2.5). Beats, not seconds — converted where it matters. */
  private polarityMix = 0;
  private prevPolarity: HazardPolarity = 'rim';
  /** Beat at which polarity last **changed** — not the last spawn. Recording the latter latched
   *  the scheduler into whichever polarity it happened to reach first. */
  private prevPolarityFlipBeat = -99;
  private lastAxisBeat = -99;
  /** The sector's cumulative allow-list (FS-08 §4.4). Empty = everything. */
  private allow: Set<HazardKind> | null = null;
  /** The live chart never runs out — sectors end on their duration or their boss, not on content. */
  readonly done = false;

  constructor(
    private rng: Rng,
    private clock: AudioClock,
  ) {}

  /** A sector may only spawn kinds in its allow-list — the introduction schedule (FS-08 R18). */
  setAllowList(kinds: readonly HazardKind[]): void {
    this.allow = new Set(kinds);
  }

  reset(): void {
    // Start a few beats ahead so nothing is due at t=0.
    this.nextBeatIndex = 4;
    this.prevNodeTheta = 0;
    this.prevPolarity = 'rim';
    this.prevPolarityFlipBeat = -99;
    this.lastAxisBeat = -99;
  }

  /**
   * The movement's `axis` fraction (FS-08 R34, §4.4.1). Set per segment from the sector definition;
   * the schedule's ceilings are what keep twelve kinds *and* two polarities learnable.
   */
  setPolarityMix(mix: number): void {
    this.polarityMix = clamp(mix, 0, 1);
  }

  /** Grid density in beats between spawns, tighter as intensity rises. */
  private density(intensity: number): number {
    if (intensity < 0.35) return 1;
    if (intensity < 0.7) return 0.5;
    return 0.5;
  }

  /** Called each sim step. Invokes `emit` for every hazard now due to spawn. */
  update(director: Director, emit: (req: SpawnRequest) => void): void {
    const leadTime = Math.abs(SPAWN_Z) / Math.max(director.scrollSpeed, 1);
    const now = this.clock.time;

    // Spawn any target beats whose (targetTime - leadTime) has arrived.
    for (let guard = 0; guard < 16; guard++) {
      const density = this.density(director.intensity);
      const targetBeat = this.nextBeatIndex * density + 4;
      const targetTime = this.clock.timeOfBeat(targetBeat);
      if (targetTime - leadTime > now) break;

      this.emitSpawn(targetBeat, director, emit);
      this.nextBeatIndex++;
    }
  }

  private eligible(k: KindWeight, intensity: number): boolean {
    if (this.allow && !this.allow.has(k.kind)) return false;
    return intensity >= k.minIntensity && (k.maxIntensity === undefined || intensity <= k.maxIntensity);
  }

  private pickKind(intensity: number): HazardKindDef {
    let total = 0;
    for (const k of KIND_TABLE) if (this.eligible(k, intensity)) total += k.weight;
    let roll = this.rng.next() * total;
    for (const k of KIND_TABLE) {
      if (!this.eligible(k, intensity)) continue;
      roll -= k.weight;
      if (roll <= 0) return HAZARD_KINDS[k.kind];
    }
    return HAZARD_KINDS.SineRibbon;
  }

  private emitSpawn(targetBeat: number, director: Director, emit: (req: SpawnRequest) => void): void {
    const intensity = director.intensity;
    const def = this.pickKind(intensity);
    const polarity = this.pickPolarity(def, targetBeat, intensity);

    // Node position: reachable step from the previous node.
    const maxJump = 1.25;
    const theta = wrap2pi(this.prevNodeTheta + this.rng.range(-maxJump, maxJump));
    this.prevNodeTheta = theta;

    configureParams(def, intensity, theta, this.rng, this.clock, _params, polarity);
    this.lastNodeTheta = _params.nodeTheta;
    this.lastNodeHalfTheta = _params.nodeHalfTheta;

    if (this.coversAxis(def, _params)) this.lastAxisBeat = targetBeat;
    if (_params.polarity !== this.prevPolarity) this.prevPolarityFlipBeat = targetBeat;
    this.prevPolarity = _params.polarity;
    emit({ def, params: _params, z: SPAWN_Z, targetBeat, intensity });
  }

  /**
   * Does this hazard threaten the tunnel axis (FS-08 R33)? Only `axis` polarity at `reach >= 0.5`
   * and the full-disc slab count. `PulseRing`/`FeedbackLoop` are explicitly excluded (FS-06 R41):
   * the region they leave open *is* the axis, so scheduling them against a centre-parked ship
   * rewards the behaviour the rule exists to forbid.
   */
  private coversAxis(def: HazardKindDef, p: HazardParams): boolean {
    if (def.radialClass === 'full') return true;
    if (def.kind === 'PulseRing' || def.kind === 'FeedbackLoop') return false;
    return p.polarity === 'axis' && p.reach >= 0.5;
  }

  /**
   * Choose polarity for this spawn (FS-08 R34, R35).
   *
   * Two rules, in priority order:
   *  1. **Axis pressure** — if `AXIS_PRESSURE_PERIOD` beats have passed since the last axis-covering
   *     hazard and intensity is at or above the gate, force `axis`. This is what makes "the middle
   *     is not safe" a property of the stream rather than of luck.
   *  2. **Flip spacing** — never place opposite polarity within `POLARITY_FLIP_MIN_BEATS` *in
   *     seconds*. A beat count silently passes at 200 BPM, which is exactly where sector 7 lives.
   *
   * Nothing here reads ship state: the guarantee is structural, verified offline, never a reaction
   * to where the player is (FS-08 R37).
   */
  private pickPolarity(def: HazardKindDef, targetBeat: number, intensity: number): HazardPolarity {
    if (!def.polarities.includes('axis')) return 'rim';

    // What we would like this hazard to be…
    const overdue = targetBeat - this.lastAxisBeat >= AXIS_PRESSURE_PERIOD;
    const wanted: HazardPolarity =
      intensity >= AXIS_PRESSURE_MIN_INTENSITY && overdue
        ? 'axis'
        : this.rng.next() < this.polarityMix
          ? 'axis'
          : 'rim';

    if (wanted === this.prevPolarity) return wanted;

    // …and whether a *flip* is reachable yet. Measured in seconds against the tempo map, because a
    // beat count silently passes at 200 BPM, which is exactly where sector 7 lives (Bible §7.2).
    const secondsPerBeat = 60 / Math.max(this.clock.bpm, 1);
    const sinceFlip = (targetBeat - this.prevPolarityFlipBeat) * secondsPerBeat;
    if (sinceFlip < POLARITY_FLIP_MIN_BEATS * secondsPerBeat) return this.prevPolarity;
    return wanted;
  }
}
